/**
 * Utility functions for calculating subdivision limits based on time signatures
 */

/**
 * Calculate maximum beats based on time signature
 * @param {string} timeSignature - Time signature in format "4/4", "6/8", etc.
 * @returns {number} Maximum number of beats allowed
 */
export const calculateMaxBeats = (timeSignature) => {
  if (!timeSignature || typeof timeSignature !== 'string') {
    return 4; // Default fallback
  }

  const parts = timeSignature.split('/');
  if (parts.length !== 2) {
    return 4; // Default fallback for invalid format
  }

  const [numerator, denominator] = parts.map(Number);
  
  if (isNaN(numerator) || isNaN(denominator)) {
    return 4; // Default fallback for invalid numbers
  }

  // The numerator directly tells us the number of beats per measure
  return numerator;
};

/**
 * Calculate maximum subdivisions based on time signature
 * @param {string} timeSignature - Time signature in format "4/4", "6/8", etc.
 * @returns {number} Maximum number of subdivisions allowed
 */
export const calculateMaxSubdivisions = (timeSignature) => {
  if (!timeSignature || typeof timeSignature !== 'string') {
    return 4; // Default fallback
  }

  const parts = timeSignature.split('/');
  if (parts.length !== 2) {
    return 4; // Default fallback for invalid format
  }

  const [numerator, denominator] = parts.map(Number);
  
  if (isNaN(numerator) || isNaN(denominator)) {
    return 4; // Default fallback for invalid numbers
  }

  // Simple time signatures (denominator = 4): quarter note gets the beat
  // Each beat subdivides into 4 sixteenth notes
  if (denominator === 4) {
    return 4;
  }
  
  // Compound time signatures (denominator = 8): eighth note gets the beat
  // Typically grouped in 3s (dotted quarter note patterns)
  if (denominator === 8) {
    return 3;
  }
  
  // Half note time signatures (denominator = 2)
  // Each beat subdivides into 4 eighth notes
  if (denominator === 2) {
    return 4;
  }
  
  // Sixteenth note time signatures (denominator = 16)
  // Each beat subdivides into 4 sixty-fourth notes
  if (denominator === 16) {
    return 4;
  }
  
  // Default to 4 subdivisions for other cases
  return 4;
};

/**
 * Get time signature for a specific bar from time signature changes list
 * @param {number} bar - Bar number (1-based)
 * @param {Array} timeSignatures - Array of time signature changes
 * @returns {string} Time signature for the specified bar
 */
export const getTimeSignatureForBar = (bar, timeSignatures) => {
  // Default time signature if no changes are defined
  let currentTimeSignature = "4/4";

  // Sort time signatures by position to ensure correct order
  const sortedTimeSignatures = [...timeSignatures].sort((a, b) => {
    // Handle both old format (measure) and new format (position.bar)
    const aBar = a.position ? a.position.bar : a.measure;
    const bBar = b.position ? b.position.bar : b.measure;
    return aBar - bBar;
  });

  // Find the most recent time signature change at or before the specified bar
  sortedTimeSignatures.forEach(ts => {
    const tsBar = ts.position ? ts.position.bar : ts.measure;
    const tsValue = ts.position ? ts.value : ts.timeSignature;
    
    if (tsBar <= bar) {
      currentTimeSignature = tsValue;
    }
  });

  return currentTimeSignature;
};
